import * as React from 'react'

import { XIcon } from 'lucide-react'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList, toggleClass } from 'react-classlist-helper'

import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import Building from '~/client/src/shared/models/LocationObjects/Building'
import Gate from '~/client/src/shared/models/LocationObjects/Gate'
import OffloadingEquipment from '~/client/src/shared/models/LocationObjects/OffloadingEquipment'
import Route from '~/client/src/shared/models/LocationObjects/Route'
import Sitemap from '~/client/src/shared/models/Sitemap'
import BuildingsStore from '~/client/src/shared/stores/domain/Buildings.store'
import GatesStore from '~/client/src/shared/stores/domain/Gates.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import RoutesStore from '~/client/src/shared/stores/domain/Routes.store'
import ZonesStore from '~/client/src/shared/stores/domain/Zones.store'

import Area from '../../models/LocationObjects/Area'
import Level from '../../models/LocationObjects/Level'
import LocationBase from '../../models/LocationObjects/LocationBase'
import Zone from '../../models/LocationObjects/Zone'
import AreasStore from '../../stores/domain/Areas.store'
import LevelsStore from '../../stores/domain/Levels.store'
import ProjectLocationsStore from '../../stores/domain/ProjectLocations.store'
import { NOOP } from '../../utils/noop'

import './MapAttributes.scss'

const OVERFLOW_LIST_ROWS_COUNT = 2
const OVERFLOW_ITEMS_LABEL_WIDTH = 141
const MIN_CONTAINER_WIDTH = 300

const xTags = (x: number) => `${x} tag${x === 1 ? '' : 's'}`

interface IProps {
  sitemap: Sitemap
  className?: string
  gatesStore?: GatesStore
  buildingsStore?: BuildingsStore
  projectLocationsStore?: ProjectLocationsStore
  zonesStore?: ZonesStore
  routesStore?: RoutesStore
  offloadingEquipmentsStore?: OffloadingEquipmentsStore
  levelsStore?: LevelsStore
  areasStore?: AreasStore
  shouldHideCrossIcons?: boolean
  areTagsBordered?: boolean
  shouldRenderAsOverflowList?: boolean
  addTagButtonRenderer?: () => JSX.Element
  isSingleMode?: boolean
}

@inject(
  'gatesStore',
  'zonesStore',
  'buildingsStore',
  'routesStore',
  'offloadingEquipmentsStore',
  'levelsStore',
  'areasStore',
  'projectLocationsStore',
)
@observer
export default class SitemapAttributes extends React.Component<IProps> {
  @observable private localProps: IProps = null
  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  @computed
  private get sitemapAssignedGates(): Gate[] {
    const { sitemap, gatesStore } = this.localProps
    return gatesStore.list.filter(g => g.isSitemapAssigned(sitemap.id))
  }

  @computed
  private get sitemapAssignedRoutes(): Route[] {
    const { sitemap, routesStore } = this.localProps
    return routesStore.list.filter(r => r.isSitemapAssigned(sitemap.id))
  }

  @computed
  private get sitemapAssignedEquipment(): OffloadingEquipment[] {
    const { sitemap, offloadingEquipmentsStore } = this.localProps
    return offloadingEquipmentsStore.list.filter(g =>
      g.isSitemapAssigned(sitemap.id),
    )
  }

  @computed
  private get sitemapAssignedBuildings(): Building[] {
    const { sitemap, buildingsStore } = this.localProps
    return buildingsStore.list.filter(b => b.isSitemapAssigned(sitemap.id))
  }

  @computed
  private get sitemapAssignedZones(): Zone[] {
    const { sitemap, zonesStore } = this.localProps
    return zonesStore.list.filter(z => z.isSitemapAssigned(sitemap.id))
  }

  @computed
  private get sitemapAssignedLevels(): Level[] {
    const { sitemap, levelsStore } = this.localProps
    return levelsStore.list.filter(l => l.isSitemapAssigned(sitemap.id))
  }

  @computed
  private get sitemapAssignedAreas(): Area[] {
    const { sitemap, areasStore } = this.localProps
    return areasStore.list.filter(a => a.isSitemapAssigned(sitemap.id))
  }

  public render() {
    const { className, shouldRenderAsOverflowList } = this.props

    if (shouldRenderAsOverflowList) {
      return this.renderAsOverflowList()
    }

    return (
      <div className={className}>
        {this.deliveryAttributes.map(item => item)}
      </div>
    )
  }

  private renderAsOverflowList = () => {
    const { addTagButtonRenderer, className } = this.props

    return (
      <DynamicOverflowList
        items={this.deliveryAttributes}
        rowsCount={OVERFLOW_LIST_ROWS_COUNT}
        additionalWidth={OVERFLOW_ITEMS_LABEL_WIDTH}
        minContainerWidthConstraint={MIN_CONTAINER_WIDTH}
      >
        {({ visibleElements, overflowElements, containerRefSetter }) => {
          return (
            <div className={className} ref={containerRefSetter}>
              <div className="inline-block">
                {this.renderOverflowLabel(overflowElements)}
                {addTagButtonRenderer && addTagButtonRenderer()}
              </div>
              {visibleElements}
            </div>
          )
        }}
      </DynamicOverflowList>
    )
  }

  private renderAttributeTag(
    text: string,
    onClick?: () => void,
    key?: React.Key,
    dataObject?: LocationBase,
    shouldUseEllipsisClass?: boolean,
  ) {
    const { shouldHideCrossIcons, areTagsBordered } = this.localProps

    return (
      <div
        className="inline-block vertical-align-top pr8 attribute-tag-holder"
        key={key}
      >
        <SitemapAttributeTag
          shouldShowAsTag={true}
          isBordered={areTagsBordered}
          dataObject={dataObject}
          contentContainerClassName={toggleClass(
            'overflow-hidden',
            !!shouldUseEllipsisClass,
          )}
        >
          <div className="tw:flex tw:items-center">
            <div
              className={classList({
                'text large tw:flex-1': true,
                ellipsis: shouldUseEllipsisClass,
              })}
            >
              {text}
            </div>
            {!shouldHideCrossIcons && onClick && (
              <XIcon
                onClick={onClick}
                className="attribute-icon tw:size-3 tw:text-foreground"
              />
            )}
          </div>
        </SitemapAttributeTag>
      </div>
    )
  }

  private renderOverflowLabel = (hiddenElements: JSX.Element[]) => {
    if (!hiddenElements || !hiddenElements.length) {
      return null
    }

    return (
      <Popover>
        <PopoverTrigger
          nativeButton={false}
          render={
            <div className="vertical-align-middle overflow-tags-btn text center large bold white pointer px10 inline-flex nowrap" />
          }
        >
          +{xTags(hiddenElements.length)}
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="overflow-tags tw:w-auto tw:gap-0 tw:overflow-auto"
        >
          {hiddenElements.map(item => item)}
        </PopoverContent>
      </Popover>
    )
  }

  @computed
  private get deliveryAttributes(): JSX.Element[] {
    const elements = this.getDeliveryAttributesElements(
      this.localProps.shouldRenderAsOverflowList,
    )

    if (!elements.length) {
      return [this.renderDefaultTag()]
    }

    if (!this.localProps.isSingleMode) {
      return elements
    }

    return [elements[0]]
  }

  private getDeliveryAttributesElements(
    shouldUseEllipsisClass?: boolean,
  ): JSX.Element[] {
    return [
      ...this.sitemapAssignedBuildings.map(building =>
        this.renderAttributeTag(
          building.name,
          this.clearBuildingSitemap.bind(this, building),
          building.id,
          building,
          shouldUseEllipsisClass,
        ),
      ),
      ...this.sitemapAssignedGates.map(gate =>
        this.renderAttributeTag(
          gate.name,
          this.clearGateSitemap.bind(this, gate),
          gate.id,
          gate,
          shouldUseEllipsisClass,
        ),
      ),
      ...this.sitemapAssignedZones.map(area =>
        this.renderAttributeTag(
          area.name,
          this.clearZoneSitemap.bind(this, area),
          area.id,
          area,
          shouldUseEllipsisClass,
        ),
      ),
      ...this.sitemapAssignedRoutes.map(route =>
        this.renderAttributeTag(
          route.name,
          this.clearRouteSitemap.bind(this, route),
          route.id,
          route,
          shouldUseEllipsisClass,
        ),
      ),
      ...this.sitemapAssignedEquipment.map(equipment =>
        this.renderAttributeTag(
          equipment.name,
          this.clearEquipmentSitemap.bind(this, equipment),
          equipment.id,
          equipment,
          shouldUseEllipsisClass,
        ),
      ),
      ...this.sitemapAssignedLevels.map(level =>
        this.renderAttributeTag(
          level.name,
          this.clearLevelSitemap.bind(this, level),
          level.id,
          level,
          shouldUseEllipsisClass,
        ),
      ),
      ...this.sitemapAssignedAreas.map(area =>
        this.renderAttributeTag(
          area.name,
          this.clearAreaSitemap.bind(this, area),
          area.id,
          area,
          shouldUseEllipsisClass,
        ),
      ),
    ]
  }

  private clearGateSitemap(dto: Gate) {
    const { sitemap, gatesStore } = this.props
    dto.deassignSitemap(sitemap.id)
    gatesStore.saveItem(dto)
  }

  private clearZoneSitemap(dto: Zone) {
    const { sitemap, zonesStore } = this.props
    dto.deassignSitemap(sitemap.id)
    zonesStore.saveItem(dto)
  }

  private clearEquipmentSitemap(dto: OffloadingEquipment) {
    const { sitemap, offloadingEquipmentsStore } = this.props
    dto.deassignSitemap(sitemap.id)
    offloadingEquipmentsStore.saveItem(dto)
  }

  private clearLevelSitemap(dto: Level) {
    const { sitemap, levelsStore } = this.props
    dto.deassignSitemap(sitemap.id)
    levelsStore.saveItem(dto)
  }

  private clearAreaSitemap(dto: Area) {
    const { sitemap, areasStore } = this.props
    dto.deassignSitemap(sitemap.id)
    areasStore.saveItem(dto)
  }

  private clearRouteSitemap(dto: Route) {
    const { sitemap, routesStore } = this.props
    dto.deassignSitemap(sitemap.id)
    routesStore.saveItem(dto)
  }

  private clearBuildingSitemap(dto: Building) {
    const { sitemap, buildingsStore } = this.props
    dto.deassignSitemap(sitemap.id)
    buildingsStore.saveItem(dto)
  }

  private renderDefaultTag() {
    const { id } = this.localProps.sitemap
    const projectLocation = this.localProps.projectLocationsStore?.list?.[0]
    if (projectLocation?.isSitemapAssigned(id)) {
      return this.renderAttributeTag(
        projectLocation.name,
        NOOP,
        projectLocation.name,
        projectLocation,
      )
    }

    const { firstBuilding } = this.localProps.buildingsStore
    if (firstBuilding) {
      return this.renderAttributeTag(
        firstBuilding.name,
        NOOP,
        firstBuilding.name,
        firstBuilding,
      )
    }

    return this.renderAttributeTag('Site', NOOP, id)
  }
}
